import React, { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import SunsetIcon from '@atlaskit/icon-lab/core/sunset';

import { usePublish } from '@atlaskit/rovo-triggers/main';
import { type ConversationAction, HandleActionType } from '@atlassian/rovo-conversation-actions';

export const DAILY_BRIEF_NUDGE_ID = 'daily-brief-nudge';

/** Only show the nudge if the brief was completed within the last 5 minutes. */
export const DAILY_BRIEF_NUDGE_RECENCY_MS = 5 * 60 * 1000;

const messages = defineMessages({
	dailyBriefNudgeTitle: {
		id: 'page-rovo-config-common.daily-brief-nudge-title',
		defaultMessage: 'Review your daily brief',
		description: 'Title for the Daily Brief nudge that appears next to the Rovo button',
	},
	dailyBriefNudgeSubtitle: {
		id: 'page-rovo-config-common.daily-brief-nudge-subtitle',
		defaultMessage: "See today's highlights at a glance",
		description: 'Subtitle shown below the Daily Brief nudge title',
	},
	dailyBriefNudgeIconLabel: {
		id: 'page-rovo-config-common.daily-brief-nudge-icon-label',
		defaultMessage: 'Daily brief',
		description: 'Accessible label for the icon in the Daily Brief nudge',
	},
});

/** Hook that creates and returns the Daily Brief nudge action. */
export function useDailyBriefNudgeAction(conversationId: string | null): ConversationAction {
	const { formatMessage } = useIntl();
	const publish = usePublish('ai-mate');

	const onClick = useCallback(() => {
		if (conversationId) {
			publish({
				type: 'chat-open',
				data: { channelId: conversationId },
				openChatMode: 'mini-modal',
				source: 'daily-brief-nudge',
			});
		}
	}, [conversationId, publish]);

	return useMemo(
		() => ({
			id: DAILY_BRIEF_NUDGE_ID,
			title: formatMessage(messages.dailyBriefNudgeTitle),
			secondaryTitle: formatMessage(messages.dailyBriefNudgeSubtitle),
			icon: () => (
				<SunsetIcon size="small" label={formatMessage(messages.dailyBriefNudgeIconLabel)} />
			),
			onClick,
			type: 'ai-action',
			handleType: HandleActionType.Base,
			nudgeContext: {
				id: DAILY_BRIEF_NUDGE_ID,
				selfEligible: true,
			},
		}),
		[onClick, formatMessage],
	);
}
